import { array, concat } from '@ember/helper';
import { action } from '@ember/object';
import { debounce, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service, Timeframe } from 'webapp-types';
import { sortBy } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import ClientGoalComponent from 'webapp/components/client/goal';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type { Goal, Space } from 'webapp/models-custom/index';

interface Args {
	model: Space;
	selectedTimeframe: Timeframe | null | undefined;
}

export default class ClientGoalsComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	@tracked private slidesToShow = 5;

	node?: HTMLElement;

	@cached
	get clientGoals() {
		return this.mmGoalsService.goalForClientModels.filter(
			(item) => item.spaceId === Number(this.args.model.id),
		);
	}

	private _goalsForSelectedTimeframe: Goal[] = [];

	@cached
	get goalsForSelectedTimeframe() {
		const allGoals = this.clientGoals;
		const timeframe = this.args.selectedTimeframe;
		let filteredGoals =
			timeframe === undefined
				? allGoals
				: allGoals.filter((goal) =>
						this.timeframeService.isMatchingTimeframe(goal, timeframe),
					);
		filteredGoals = sortBy(filteredGoals, 'order', 'id');

		const isChanged = this.mmGoalsService.updateModelCache(
			this._goalsForSelectedTimeframe,
			filteredGoals.map((goal) => goal.model),
		);

		if (isChanged && this.node) {
			this.restartSlider();
		}

		return this._goalsForSelectedTimeframe;
	}

	// ----- Slider -----

	private setupSlider(): void {
		if (!this.clientGoals.length) {
			return;
		}

		const node = this.node;
		if (!node) {
			return;
		}

		const nodeEl = $(node);

		nodeEl.find('ul').slick({
			speed: 500,
			autoplay: false,
			slidesToShow: this.slidesToShow,
			slidesToScroll: 1,
			infinite: false,
			arrows: true,
			prevArrow: '<a class="arrow slick-prev"><span></span></a>',
			nextArrow: '<a class="arrow slick-next"><span></span></a>',
			appendArrows: nodeEl,
			useTransform: true,
		});
	}

	private restartSlider(): void {
		const node = this.node!;

		const slickEl = $(node).find('ul');

		if (slickEl.hasClass('slick-initialized')) {
			slickEl.slick('unslick');
			slickEl.find('.goal').remove();
		}

		scheduleOnce('afterRender', this, this.setupSlider);
	}

	@action
	updateSlidesToShow(): void {
		const width = window.innerWidth;

		let newSlidesCount = 5;
		if (width < 460) {
			newSlidesCount = 1;
		} else if (width < 650) {
			newSlidesCount = 2;
		} else if (width < 900) {
			newSlidesCount = 3;
		} else if (width <= 1024) {
			newSlidesCount = 4;
		}

		if (this.slidesToShow === newSlidesCount) {
			return;
		}

		this.slidesToShow = newSlidesCount;

		const slickEl = $('ul.slick-initialized');
		if (slickEl.length) {
			slickEl.slick('slickSetOption', 'slidesToShow', this.slidesToShow);
			slickEl.slick('refresh');
		}
	}

	@action
	debounceUpdateSlidesToShow(): void {
		debounce(this, this.updateSlidesToShow, 150);
	}

	// ----- Lifecycle -----

	@action
	setupView(node: HTMLElement): void {
		this.node = node;

		this.updateSlidesToShow();
		this.setupSlider();

		window.addEventListener('resize', this.debounceUpdateSlidesToShow);
	}

	@action
	teardownView(): void {
		window.removeEventListener('resize', this.debounceUpdateSlidesToShow);
	}
	<template>
		<div
			class={{joinClasses (array "client-goals" (concat "slides-" this.slidesToShow))}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<ul>
				{{#if this.clientGoals}}
					{{#each this.goalsForSelectedTimeframe as |goal|}}
						<ClientGoalComponent @model={{goal}} />
					{{/each}}
				{{else}}
					<li class="goal fake">
						<div class="ct no-data">
							No goals set
							<ToolTipWithIconComponent
								@text="No goals set for this account. To create goals, switch to the account and navigate to 'Goals' tab in main navigation."
							/>
						</div>
					</li>
				{{/if}}
			</ul>
		</div>
	</template>
}
